import { useEffect, useMemo, useState } from 'react';
import {
  addWireDays,
  dateInLeaveFrame,
  leaveBalanceSchema,
  leaveFrameWindow,
  leaveRequestPreviewSchema,
  offerableLeaveFrames,
  toWireDate,
  wireMonday,
  type LeaveBalance,
  type LeaveFrameWindow,
  type LeaveType,
} from '@forecastify/shared';
import { Button, Dialog, Icon, Select, TextInput } from '@forecastify/ui';
import { useI18n, useT } from '../../i18n';
import { useSession } from '../../session';
import { parseList } from '../permissions-hook';
import { Field } from '../card';
import { DropZone } from '../drop-zone';
import { DOCUMENT_FILES } from '../file-kinds';
import { leaveIcon } from './people-look';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ ÚJ SZABADSÁGKÉRELEM — the mockup's dialog, which ours was not.        │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Ours was a type dropdown and two date boxes. The mockup is a picker with a running total:
 *
 * | Left                                              | Right rail                            |
 * | ------------------------------------------------- | ------------------------------------- |
 * | Típus and Keretév, side by side                    | KVÓTÁM — one card per type            |
 * | two months, click a start then an end              | keret / felhasznált / függőben / marad |
 * | *"N munkanap"* under the calendar                  |                                       |
 * | Megjegyzés, Csatolmány                             |                                       |
 *
 * ⚠️⚠️ **THE DAY COUNT COMES FROM THE SERVER ON EVERY SELECTION, AND IT HAS TO.** *"A kvótád élőben
 * követi a kijelölést"* is C44's kernel — the schedule in force, the holidays, the leave already
 * approved. A client that counted weekdays would disagree with the request it is about to create,
 * which is the one number this dialog exists to show.
 *
 * ⚠️ **Non-working days are struck through**, from the same answer: the calendar says *what a
 * selection would cost* rather than leaving somebody to guess why five days became three.
 *
 * ⚠️⚠️ **THE ATTACHMENT IS STAGED HERE AND UPLOADED AFTER THE CREATE — 15 Sep, reversing the note
 * that stood in this spot.** It read: *"a file needs something to hang off, so a picker here would
 * either hold bytes in the browser or invent a draft request."* Both halves are true and neither is
 * a reason: holding bytes in the browser for the second it takes to POST a request is what the task
 * comment composer has always done, and it is the third option the note did not consider.
 *
 * ⚠️ **So the form stages `File` objects and the route uploads them once the id exists.** Reported
 * as *"why can I only upload a document for my leave request after submitting?"* — and the honest
 * answer was *because the code was arranged that way*, which is not an answer.
 */

/**
 * ⚠️ **Last year, this year, next year** — the frames worth CONSIDERING, before any of the three
 * tests in `frameYears` has run. A frame opens on 1 January of its own year, so next year is always
 * a candidate; the year before last is dead under every carry rule this product has.
 */
const CANDIDATE_OFFSETS = [-1, 0, 1] as const;

export interface NewRequestDraft {
  readonly leaveTypeId: string;
  readonly year: number;
  readonly fromDate: string | null;
  readonly toDate: string | null;
  readonly note: string;
  /**
   * ⚠️ **`File` objects, not uploaded anything.** They are posted one at a time against the request
   * the moment it has an id — `requests-route.tsx` — which is the same order
   * `useTaskWrites.comment` uses for a comment with attachments, and for the same reason: the
   * parent has to exist before a file can point at it.
   */
  readonly files: readonly File[];
}

export function NewRequestDialog({
  open,
  types,
  balances,
  taken,
  serverError,
  onClose,
  onSubmit,
}: {
  readonly open: boolean;
  readonly types: readonly LeaveType[];
  readonly balances: readonly LeaveBalance[];
  /**
   * ┌───────────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ THE DAYS THIS PERSON HAS ALREADY ASKED FOR — 15 Sep.                 │
   * └───────────────────────────────────────────────────────────────────────────┘
   *
   * *"Days that already have a leave submitted for shouldn't show up as a valid selection."* The
   * server has always refused an overlap with `overlaps`; the calendar offered the days anyway, so
   * the refusal arrived after somebody had picked a range and pressed submit.
   *
   * ⚠️ **Pending and approved, and nothing else.** A rejected or revoked request is not booked —
   * those days are free, and greying them would hide days somebody may legitimately take.
   */
  readonly taken: readonly { readonly fromDate: string; readonly toDate: string }[];
  readonly serverError: string | null;
  readonly onClose: () => void;
  readonly onSubmit: (draft: NewRequestDraft) => void;
}) {
  const t = useT();
  const { locale } = useI18n();
  const session = useSession();

  const thisYear = new Date().getFullYear();
  /* ⚠️ The browser's date, and only for OFFERING a frame — the server refuses on its own clock. */
  const today = new Date().toISOString().slice(0, 10);
  const [leaveTypeId, setLeaveTypeId] = useState('');
  const [year, setYear] = useState(thisYear);

  const selectedType = types.find((one) => one.id === leaveTypeId);

  /**
   * ┌───────────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ WHICH DAYS THIS FRAME CAN STILL COVER — 15 Sep.                      │
   * └───────────────────────────────────────────────────────────────────────────┘
   *
   * `leaveFrameWindow` is `@forecastify/shared`'s, and the server refuses with the same function.
   * The greying is not the rule — the refusal is — but a calendar that lets somebody pick fourteen
   * days and then answers *"that frame is closed"* has wasted their afternoon.
   *
   * ⚠️ **Undefined until a type is chosen**, because the deadline is a property OF the type: two
   * types in the same workspace carry to different months, and one of them may not carry at all.
   */
  const frame =
    selectedType === undefined
      ? null
      : leaveFrameWindow(year, selectedType.carryOver, selectedType.carryToMonth);

  /**
   * ⚠️⚠️ **THE FRAMES WORTH OFFERING, RATHER THAN THREE FIXED YEARS.** It listed last year, this
   * year and next unconditionally — which is how 2025 was selectable in 2027. A year is offered
   * when its window has not closed and has opened: last year survives only while its carry deadline
   * is ahead, and next year is always available because a frame opens on 1 January of its own year
   * and somebody booking December's holiday in November is the ordinary case.
   */
  /**
   * ┌────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ THE BALANCES OF THE FRAME BEING ASKED FOR — feedback round 4 item 24.      │
   * └────────────────────────────────────────────────────────────────────┘
   *
   * The rail drew the `balances` prop, which is the LIST SCREEN's — fetched for whatever year its
   * *Keretév* filter happens to hold. So picking 2027 in this dialog left the rail showing 2026's
   * numbers, with nothing on screen saying which year they were: *"picking a frame must refresh the
   * right-hand quota"* is the item, and the reason it did not is that the rail was never this
   * dialog's to begin with.
   *
   * ⚠️ **So it reads its own, keyed on the year IT is asking for.** The prop stays as the opening
   * answer — it is right whenever the two years agree, which is most of the time — so the rail is
   * never blank while the read is in flight.
   *
   * ⚠️ **Not fatal.** A read that fails leaves the previous answer up rather than emptying the
   * rail, which is the same rule every other picker feed in this product follows.
   */
  /**
   * ⚠️⚠️ **EVERY CANDIDATE YEAR, AND IT USED TO BE ONLY THE SELECTED ONE.** Item 24 drops a
   * frame with nothing left in it, but the dialog held balances for the year it was ASKING for, so
   * it could only drop that one — and said so in as many words: *a frame is dropped when we know it
   * is empty, never merely because we have not looked.* That was honest and it was also the reason
   * an exhausted 2025 kept appearing in the list: nobody had looked at 2025.
   *
   * The product owner's ruling of 20 Sep is that an empty frame should never be offered, so the
   * dialog looks at all three. Three reads of a handful of rows, once per opening.
   */
  const [balancesByYear, setBalancesByYear] = useState<
    Readonly<Record<number, readonly LeaveBalance[]>>
  >({});

  /* ⚠️ The prop is the RAIL's opening answer and nothing more — it is the list screen's year,
     which is right whenever the two agree and is never used to drop a frame. */
  const frameBalances = balancesByYear[year] ?? balances;

  useEffect(() => {
    if (!open) return;
    setBalancesByYear({});
  }, [open]);

  useEffect(() => {
    if (!open) return;
    void (async () => {
      const answers = await Promise.all(
        CANDIDATE_OFFSETS.map(async (offset) => {
          const one = thisYear + offset;
          const response = await session
            .request(`/leave-requests/balances?year=${String(one)}`)
            .catch(() => null);
          if (response?.ok !== true) return null;
          const rows = parseList(leaveBalanceSchema, await response.json());
          return rows === null ? null : ([one, rows] as const);
        }),
      );
      /* ⚠️ One `setState` for the three, so the year filter never runs against a half-filled
         map and blinks a frame out of the list as the second answer lands. */
      setBalancesByYear(Object.fromEntries(answers.filter((entry) => entry !== null)));
    })();
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render; depending on it would refetch for ever (C20's ten-requests bug). */
  }, [open, thisYear]);

  /**
   * ⚠️⚠️ **WHAT IS LEFT IN A FRAME, OR `null` FOR *WE CANNOT SAY*.** The distinction is the
   * whole of item 24's caution: an unlimited type has no frame to exhaust, a type with no row in a
   * year has no quota there, and a read that failed tells us nothing. None of the three is a frame
   * we KNOW is empty, and only a known-empty frame is dropped.
   */
  const remainingIn = (one: number): number | null => {
    const rows = balancesByYear[one];
    if (rows === undefined) return null;
    const balance = rows.find((entry) => entry.leaveTypeId === leaveTypeId);
    if (balance?.quotaDays == null) return null;
    return balance.quotaDays - balance.usedDays - balance.pendingDays;
  };

  const [from, setFrom] = useState<string | null>(null);
  const [to, setTo] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [files, setFiles] = useState<readonly File[]>([]);
  const [month, setMonth] = useState(() => toWireDate(new Date()).slice(0, 7));

  /**
   * ┌──────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ THREE TESTS, AND A FRAME IS OFFERED ONLY IF IT PASSES ALL THREE.          │
   * └──────────────────────────────────────────────────────────────┘
   *
   * **Open** — its carry deadline is ahead of today. **Covering** — it contains the days actually
   * selected. **Non-empty** — it has a day left to spend.
   *
   * ⚠️⚠️ **THE SECOND AND THIRD ARE THE PRODUCT OWNER'S RULING OF 20 SEP**, and the screen
   * that prompted it had a September 2026 range selected while offering 2025, 2026 and 2027. 2027
   * was open and did not contain a single selected day; 2025 was open — the workspace's leave type
   * carries to month 12, so its frame runs to 31 December 2026 — and may well have been spent. Both
   * were years somebody could pick, spend a minute on, and be refused for.
   *
   * ⚠️⚠️ **THE TWO DIRECTIONS ARE ONE RULE READ FROM EITHER END.** Days first narrows the
   * year list to the frames that cover them; year first greys the days the frame cannot cover
   * (`beyond`, on the calendar below). Neither can be satisfied without the other: a person who
   * picks days and then a year must not be offered one that will be refused, and a person who
   * picks a year and then days must not be able to select days it cannot pay for.
   */
  const frameYears =
    selectedType === undefined
      ? /* ⚠️ Nothing chosen yet: the deadline is a property OF the type, so there is no window to
           test. Last year is withheld until there is — it is the one that is usually closed. */
        CANDIDATE_OFFSETS.map((offset) => thisYear + offset).filter((one) => one !== thisYear - 1)
      : offerableLeaveFrames({
          years: CANDIDATE_OFFSETS.map((offset) => thisYear + offset),
          carryOver: selectedType.carryOver,
          carryToMonth: selectedType.carryToMonth,
          today,
          /* ⚠️ A half-made range is still a selection: one end picked already rules out every
             frame that cannot cover it, and waiting for the second wastes the first click. */
          selection: from === null ? null : { from, to: to ?? from },
          remaining: remainingIn,
        });

  /**
   * ⚠️⚠️ **THE CHOSEN YEAR FOLLOWS THE LIST, OR THE DIALOG SENDS ONE IT NO LONGER OFFERS.**
   * The selection narrows the frames, so the year held in state can stop being on the list while
   * the `Select` still shows it — a control displaying a value its own options do not contain, and
   * a submit that files against a frame the picker had just ruled out. Moving to the first
   * surviving frame is what somebody picking days in one year means anyway.
   */
  const firstFrame = frameYears[0];
  useEffect(() => {
    if (firstFrame === undefined) return;
    if (frameYears.includes(year)) return;
    setYear(firstFrame);
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- the LIST is the dependency, and an
       array is a fresh object every render; its contents are what must not be re-run against. */
  }, [frameYears.join(','), year, firstFrame]);
  const [preview, setPreview] = useState<{ days: number; offDays: readonly string[] } | null>(null);

  /* ⚠️ The first active type, chosen when the dialog opens rather than on every render: a select
     that re-picks its own value fights the person using it. */
  useEffect(() => {
    if (!open) return;
    setLeaveTypeId(types[0]?.id ?? '');
    setFrom(null);
    setTo(null);
    setNote('');
    setPreview(null);
  }, [open, types]);

  /* ⚠️⚠️ The running total, from the server. One read per completed selection. */
  useEffect(() => {
    if (!open || from === null) {
      setPreview(null);
      return;
    }
    /* ⚠️⚠️ **PRICED FROM THE FIRST CLICK, NOT THE SECOND.** It waited for both ends, so a
       single picked day — the commonest request there is — showed no count and no shading at all,
       and a Saturday picked alone was filled as if it cost something. `to ?? from` is the range a
       one-day selection actually is. */
    const end = to ?? from;
    void (async () => {
      const response = await session
        .request(`/leave-requests/preview?from=${from}&to=${end}`)
        .catch(() => null);
      if (response?.ok !== true) return;
      const parsed = leaveRequestPreviewSchema.safeParse(await response.json());
      if (parsed.success) setPreview({ days: parsed.data.days, offDays: parsed.data.offDays });
    })();
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render; depending on it would refetch for ever (C20's ten-requests bug). */
  }, [open, from, to]);

  const monthsShown = useMemo(() => [month, nextMonth(month)], [month]);
  const offDays = new Set(preview?.offDays ?? []);

  /**
   * ⚠️⚠️ **A DAY ALREADY SPOKEN FOR IS NOT PICKABLE.** Built once per render from `taken`, which is
   * a handful of ranges — a `Set` of every day in them would be the same answer more expensively.
   */
  const isTaken = (date: string): boolean =>
    taken.some((one) => date >= one.fromDate && date <= one.toDate);

  /**
   * ⚠️⚠️ **WHAT THE SELECTION COSTS, AND IT CAN EXCEED THE FRAME.** The rail used to clamp the
   * remainder at zero — `Math.max(0, …)` — so asking for fourteen days against four showed *0
   * marad* and read like a tight fit rather than an impossibility. It shows the real figure now,
   * negative and in the destructive colour, and the submit button is disabled while it is.
   *
   * ⚠️ **The server refuses this too** (`quota_exceeded`), which is the boundary. This is so
   * nobody reaches it.
   */
  const asked = preview?.days ?? 0;
  const activeBalance = frameBalances.find((one) => one.leaveTypeId === leaveTypeId);
  const overdrawn =
    activeBalance !== undefined &&
    activeBalance.quotaDays !== null &&
    asked > 0 &&
    activeBalance.quotaDays - activeBalance.usedDays - activeBalance.pendingDays - asked < 0;

  const pick = (date: string): void => {
    /* ⚠️ Two clicks: the first sets the start and clears the end, the second closes the range.
       Clicking before the start restarts rather than inverting — the mockup's own behaviour. */
    if (from === null || to !== null || date < from) {
      setFrom(date);
      setTo(null);
      return;
    }
    setTo(date);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={t('leave.dialog.new')}
      description={t('leave.dialog.newHint')}
      width="min(960px, 96vw)"
      closeLabel={t('common.close')}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            /* ⚠️ Overdrawn blocks the submit — see `overdrawn`. A button that stays pressable for
               a request the server will refuse is a button that teaches people to distrust it. */
            /* ⚠️ And blocked with no frame to file against — item 24: the picker offers none
               when every one is exhausted, so there is no year to send. */
            disabled={leaveTypeId === '' || from === null || overdrawn || frameYears.length === 0}
            onClick={() => {
              if (leaveTypeId === '' || from === null || overdrawn) return;
              if (frameYears.length === 0) return;
              onSubmit({ leaveTypeId, year, fromDate: from, toDate: to ?? from, note, files });
            }}
          >
            {t('leave.dialog.submit')}
          </Button>
        </>
      }
    >
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_260px]">
        <div className="flex min-w-0 flex-col gap-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('leave.field.type')}>
              <Select
                value={leaveTypeId}
                onChange={(event) => {
                  setLeaveTypeId(event.target.value);
                }}
                options={types.map((type) => ({ value: type.id, label: type.name }))}
              />
            </Field>
            {/* ⚠️⚠️ **A SENTENCE WHEN THERE IS NO FRAME TO OFFER** — item 24 drops an exhausted
                one, and *every* frame being exhausted is the case that produces an empty picker.
                A `Select` with no options is a control that looks broken; the sentence says what
                is actually true, which is that this type has nothing left to ask against. */}
            {frameYears.length === 0 ? (
              <Field label={t('leave.column.year')}>
                <p className="text-muted-foreground pt-1.5 text-[12px]">
                  {t('leave.dialog.noFrame')}
                </p>
              </Field>
            ) : (
              <Field label={t('leave.column.year')}>
                <Select
                  value={String(year)}
                  onChange={(event) => {
                    setYear(Number(event.target.value));
                  }}
                  options={frameYears.map((one) => ({
                    value: String(one),
                    label: String(one),
                  }))}
                />
              </Field>
            )}
          </div>

          {/* ── the two months ─────────────────────────────────────────────────────────────── */}
          <div className="rounded-xl border" data-tour="leave-calendar">
            <div className="flex items-center gap-2 border-b px-3 py-2">
              <Button
                variant="outline"
                size="icon-sm"
                aria-label={t('home.chart.previous')}
                onClick={() => {
                  setMonth(previousMonth(month));
                }}
              >
                <Icon name="chevron-left" size={14} />
              </Button>
              <div className="flex flex-1 justify-around">
                {monthsShown.map((one) => (
                  <span key={one} className="text-[12.5px] font-medium">
                    {monthLabel(one, locale)}
                  </span>
                ))}
              </div>
              <Button
                variant="outline"
                size="icon-sm"
                aria-label={t('home.chart.next')}
                onClick={() => {
                  setMonth(nextMonth(month));
                }}
              >
                <Icon name="chevron-right" size={14} />
              </Button>
            </div>

            <div className="grid gap-4 p-3 sm:grid-cols-2">
              {monthsShown.map((one) => (
                <MonthGrid
                  key={one}
                  month={one}
                  from={from}
                  to={to}
                  offDays={offDays}
                  isTaken={isTaken}
                  bookedLabel={t('leave.dialog.alreadyBooked')}
                  frame={frame}
                  weekdays={t('calendar.weekdays').split(',')}
                  onPick={pick}
                />
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-2 border-t px-3 py-2">
              <span className="text-[12.5px] font-medium">{t('leave.dialog.pickDays')}</span>
              <span className="text-muted-foreground text-[12px]">
                {from === null
                  ? t('leave.dialog.nothingPicked')
                  : preview === null
                    ? `${from} – ${to ?? from}`
                    : `${from} – ${to ?? from} · ${t('leave.amount.days', { days: preview.days })}`}
              </span>
              {/* ⚠️ The reason the submit is disabled, beside the selection that caused it — a
                  greyed button with no sentence is a dead end somebody has to guess at. */}
              {!overdrawn ? null : (
                <span className="text-destructive ml-auto text-[12px] font-medium">
                  {t('leave.dialog.overdrawn')}
                </span>
              )}
            </div>
          </div>

          <Field label={t('leave.field.note')} tourId="leave-note">
            <TextInput
              value={note}
              placeholder={t('leave.dialog.notePlaceholder')}
              onChange={(event) => {
                setNote(event.target.value);
              }}
            />
          </Field>

          <Field
            label={t('leave.field.attachment')}
            {...(selectedType?.attachmentRule === 'required'
              ? { note: t('leave.dialog.attachmentRequiredNote') }
              : {})}
          >
            <DropZone
              accept={DOCUMENT_FILES}
              onFiles={(chosen) => {
                setFiles((current) => [...current, ...chosen]);
              }}
            />
          </Field>

          {files.length === 0 ? null : (
            <ul className="flex flex-wrap gap-1.5">
              {files.map((file, index) => (
                <li
                  key={`${file.name}-${String(index)}`}
                  className="bg-muted flex items-center gap-1.5 rounded-md border px-2 py-1 text-[11.5px]"
                >
                  <Icon name="paperclip" size={12} className="text-muted-foreground" />
                  <span className="max-w-[180px] truncate">{file.name}</span>
                  <button
                    type="button"
                    aria-label={t('task.comment.attachRemove', { name: file.name })}
                    className="text-muted-foreground hover:text-foreground"
                    onClick={() => {
                      setFiles((current) => current.filter((_, at) => at !== index));
                    }}
                  >
                    <Icon name="x" size={12} />
                  </button>
                </li>
              ))}
            </ul>
          )}

          {serverError === null ? null : (
            <p className="text-destructive text-[12px]">{serverError}</p>
          )}
        </div>

        {/* ── KVÓTÁM ─────────────────────────────────────────────────────────────────────── */}
        <div className="flex flex-col gap-2" data-tour="leave-quota">
          <div className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
            {/* ⚠️ The heading carries the YEAR since item 24, because the rail is now about ONE
                frame and a panel of figures with no year on it is a panel nobody can check. */}
            {t('leave.dialog.myQuotaIn', { year })}
          </div>
          {/**
           * ┌────────────────────────────────────────────────────────────────────┐
           * │ ⚠️⚠️ ONE CARD, NOT ONE PER TYPE — feedback round 4 item 24.                 │
           * └────────────────────────────────────────────────────────────────────┘
           *
           * The mockup draws a card per leave type and we transcribed that. What it costs is the
           * question this rail exists to answer: *what will I have left if I submit this?* Five
           * cards, four of them about types nobody is asking for, with the live subtraction on one
           * of them — so the figure that moves is somewhere in a list of figures that do not.
           *
           * ⚠️ **The type picker is what chooses between them**, three inches to the left, and has
           * been since the dialog was built. A second way to compare types would be a second
           * control; this is the ANSWER panel.
           *
           * ⚠️ **`frameBalances`, not the `balances` prop** — the year this dialog is asking for,
           * rather than the year the list screen's filter happens to hold. See its declaration.
           */}
          {/* ⚠️ A type with no quota row in this frame has nothing to draw — said, rather than
              left as an empty rail, which reads as a panel that failed to load. */}
          {frameBalances.some((balance) => balance.leaveTypeId === leaveTypeId) ? null : (
            <p className="text-muted-foreground rounded-lg border p-2.5 text-[11.5px]">
              {t('leave.dialog.noQuotaRow')}
            </p>
          )}
          {frameBalances
            .filter((balance) => balance.leaveTypeId === leaveTypeId)
            .map((balance) => {
              const frame = balance.quotaDays;
              const available =
                frame === null ? 0 : Math.max(0, frame - balance.usedDays - balance.pendingDays);
              const pct = (value: number): number =>
                frame === null || frame === 0
                  ? 0
                  : Math.max(0, Math.min(100, Math.round((value / frame) * 100)));
              return (
                <div
                  key={balance.leaveTypeId}
                  className="border-primary bg-primary/5 rounded-lg border p-2.5"
                >
                  <div className="flex items-center gap-2">
                    <Icon
                      name={leaveIcon(types.find((one) => one.id === balance.leaveTypeId)?.icon)}
                      size={13}
                      className="text-muted-foreground"
                    />
                    <span className="min-w-0 flex-1 truncate text-[12.5px] font-medium">
                      {balance.leaveTypeName}
                    </span>
                  </div>
                  {frame === null ? (
                    <p className="text-muted-foreground mt-1 text-[11.5px]">
                      {t('leave.balance.unlimited')}
                    </p>
                  ) : (
                    <>
                      <div className="bg-muted mt-2 flex h-1.5 overflow-hidden rounded-full">
                        <span
                          className="bg-primary h-full"
                          style={{ width: `${String(pct(available))}%` }}
                        />
                        <span
                          className="h-full"
                          style={{
                            width: `${String(pct(balance.pendingDays))}%`,
                            background: 'var(--status-warn)',
                          }}
                        />
                      </div>
                      <dl className="text-muted-foreground mt-2 flex flex-col gap-0.5 text-[11px]">
                        <QuotaLine label={t('leave.dialog.frame')} value={frame} />
                        {/**
                         * ⚠️⚠️ **WHERE PART OF THE FRAME CAME FROM — feedback round 4 item 25.** The
                         * frame above already INCLUDES the whole days overtime earned, so this is a
                         * breakdown rather than a figure to add — exactly as the carried days are.
                         *
                         * ⚠️ **Absent at zero**, because *"0 from overtime"* is a line about a feature
                         * this workspace may not use at all.
                         */}
                        {balance.accruedTakeable === 0 ? null : (
                          <QuotaLine
                            label={t('leave.balance.accrued')}
                            value={balance.accruedTakeable}
                          />
                        )}
                        <QuotaLine label={t('leave.detail.used')} value={balance.usedDays} />
                        <QuotaLine label={t('leave.status.pending')} value={balance.pendingDays} />
                        <QuotaLine
                          label={t('leave.dialog.remaining')}
                          value={
                            /* ⚠️⚠️ The selection is subtracted LIVE — and since item 24 the
                             only card drawn IS the type being asked for. Somebody sees the frame they
                             would be left with before they submit. ⚠️ **Not clamped at zero** —
                             see `overdrawn`: the real figure is the one that says no. */
                            preview === null ? available : available - preview.days
                          }
                          strong
                          negative={preview !== null && available - preview.days < 0}
                        />
                      </dl>
                    </>
                  )}
                </div>
              );
            })}
        </div>
      </div>
    </Dialog>
  );
}

function QuotaLine({
  label,
  value,
  strong,
  negative,
}: {
  readonly label: string;
  readonly value: number;
  readonly strong?: boolean;
  /** ⚠️ The selection costs more than the frame has — the figure is the refusal. */
  readonly negative?: boolean;
}) {
  const t = useT();
  return (
    <div
      className={`flex items-center justify-between ${strong === true ? 'text-foreground' : ''} ${
        negative === true ? 'text-destructive font-medium' : ''
      }`}
    >
      <dt>{label}</dt>
      <dd className="tabular-nums">
        {value} {t('home.quota.days')}
      </dd>
    </div>
  );
}

/** One month of day buttons — Monday first, like every other calendar in the product. */
function MonthGrid({
  month,
  from,
  to,
  offDays,
  isTaken,
  weekdays,
  frame,
  bookedLabel,
  onPick,
}: {
  readonly month: string;
  readonly from: string | null;
  readonly to: string | null;
  readonly offDays: ReadonlySet<string>;
  /** ⚠️ Whether this day already belongs to a request — see `NewRequestDialog.taken`. */
  readonly isTaken: (date: string) => boolean;
  readonly weekdays: readonly string[];
  /**
   * ⚠️ The chosen frame's window, or `null` before a type is picked. Days outside it are drawn
   * disabled — `leaveFrameWindow` in `@forecastify/shared` is the same rule the server refuses on.
   */
  readonly frame: LeaveFrameWindow | null;
  /** Already translated — the tooltip on a day another request already holds. */
  readonly bookedLabel: string;
  readonly onPick: (date: string) => void;
}) {
  const first = `${month}-01`;
  const start = wireMonday(first) ?? first;
  const cells = Array.from({ length: 42 }, (_, index) => addWireDays(start, index)).filter(
    (date): date is string => date !== null,
  );

  return (
    <div>
      <div className="text-muted-foreground grid grid-cols-7 gap-0.5 text-center text-[10.5px]">
        {weekdays.map((day) => (
          <span key={day}>{day}</span>
        ))}
      </div>
      <div className="mt-1 grid grid-cols-7 gap-0.5">
        {cells.map((date) => {
          const outside = date.slice(0, 7) !== month;
          const off = offDays.has(date);
          /**
           * ⚠️⚠️ **ONLY THE DAYS THAT ACTUALLY COST SOMETHING ARE FILLED — 15 Sep.** The range used
           * to paint every day between the two ends, weekends and public holidays included, so a
           * Monday-to-Monday selection looked like eight days against a count that said six. The
           * fill is now the answer, not the gesture: a day inside the range that the kernel did not
           * charge for is struck through like any other non-working day.
           *
           * ⚠️ `offDays` is the SERVER's list, from the same preview the count comes from — so the
           * shading and the figure under the calendar cannot disagree.
           */
          const inRange =
            from !== null && (to === null ? date === from : date >= from && date <= to);
          const selected = inRange && !off;
          /**
           * ⚠️⚠️ **DISABLED, NOT STRUCK THROUGH.** A non-working day is struck and still pickable —
           * a range may legitimately span a weekend, it just costs nothing. A day the frame cannot
           * cover is a different statement: it is not available at all, and a control that looks
           * pickable and refuses is worse than one that says so.
           */
          const beyond = frame !== null && !dateInLeaveFrame(date, frame);
          /* ⚠️ Already asked for — the same treatment as beyond the frame, because it is the same
             statement: not available, rather than available and free. */
          const booked = isTaken(date);
          return (
            <button
              key={date}
              type="button"
              disabled={beyond || booked}
              /* ⚠️ The booked day says WHY in its tooltip: a disabled control with no reason is a
                 dead end, and this is the one somebody will ask about. */
              {...(booked ? { title: bookedLabel } : {})}
              className={`h-7 rounded-md text-[11.5px] tabular-nums transition-colors ${
                outside ? 'text-muted-foreground/40' : ''
              } ${selected ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'} ${
                (off || booked) && !selected ? 'text-muted-foreground/60 line-through' : ''
              } ${beyond || booked ? 'text-muted-foreground/30 cursor-not-allowed' : ''} ${
                booked ? 'bg-muted/60' : ''
              }`}
              onClick={() => {
                onPick(date);
              }}
            >
              {Number(date.slice(8))}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function nextMonth(month: string): string {
  const [year, index] = month.split('-').map(Number);
  const date = new Date(Number(year), Number(index), 1);
  return toWireDate(date).slice(0, 7);
}

function previousMonth(month: string): string {
  const [year, index] = month.split('-').map(Number);
  const date = new Date(Number(year), Number(index) - 2, 1);
  return toWireDate(date).slice(0, 7);
}

function monthLabel(month: string, locale: string): string {
  return new Date(`${month}-01T00:00:00`).toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
  });
}
